import { useMemo, useState } from 'react';
import { cn } from '../lib/utils';
import { markActivity } from '../lib/idleActivity';

/**
 * The box around a Stripe Element — the wrapper counterpart to `fieldClassName`.
 *
 * A Stripe Element is an iframe served from js.stripe.com that paints nothing but the text
 * inside it: the border, radius, height and fill a customer reads as "the input" are ours, on
 * the div around it. Seven call sites used to spell that div out by hand, all seven landing on a
 * 14px corner, a 40px box and the panel fill — next to native inputs with a 10px corner, a 44px
 * box and the field fill. This is the one place that shape is decided now, so a card field and
 * the Cardholder Name input above it stay the same control.
 *
 * Focus lives here too. A native field gets its ring from CSS `:focus-visible`, which cannot
 * cross into the iframe, so the Element's own focus/blur events are lifted onto this div — the
 * `onFocus`/`onBlur` in the props handed to the render function below. Miss them and the field
 * is the only one on the form that stays dark when the caret is in it.
 *
 * Typing in the iframe also never reaches our page's keyboard listeners, so a customer entering card
 * details would look idle to the idle auto-logout. Focus and every `change` (one per keystroke) are
 * therefore recorded as activity here. That is why a caller's own `onChange` is passed to this
 * component rather than to the Element: the render-function props would otherwise override it.
 *
 * @param {object} props
 * @param {(fieldProps: { onFocus: () => void, onBlur: () => void, onChange: (event: object) => void, className: string }) => React.ReactNode} props.children
 *   Render function receiving the props to spread onto the Element. Free to render more than the
 *   Element alone — the box is a flex row, so a leading icon is just a sibling.
 * @param {(event: object) => void} [props.onChange] The Element's change handler.
 * @param {boolean} [props.hasError]  Outlines the box in the danger colour.
 * @param {boolean} [props.disabled]  Dims the box to match the Element's own disabled option.
 * @param {string}  [props.className] Layout for the box itself (spacing, width) — as with Input,
 *   the caller owns its margin.
 * @param {string}  [props.dataTestId]
 */
export function StripeField({ children, onChange, hasError = false, disabled = false, className, dataTestId }) {
  const [isFocused, setIsFocused] = useState(false);

  // Memoised so the Element is handed the same props unless the caller's onChange changes;
  // react-stripe-js re-attaches an event handler whose identity changes.
  const fieldProps = useMemo(
    () => ({
      onFocus: () => {
        setIsFocused(true);
        markActivity();
      },
      onBlur: () => setIsFocused(false),
      onChange: (event) => {
        markActivity();
        onChange?.(event);
      },
      // The Element renders its own div; without this it will not stretch inside the flex row.
      className: 'flex-1 min-w-0',
    }),
    [onChange]
  );

  return (
    <div
      className={cn(
        'flex h-11 w-full items-center gap-2 rounded-lg border bg-surface-filter px-3 transition-[color,box-shadow]',
        // The same ring the global :focus-visible rule (theme.css) draws on a native field.
        isFocused && 'outline-2 outline-offset-2 outline-focus-ring',
        hasError ? 'border-danger-fg' : 'border-line-input',
        disabled && 'opacity-60',
        className
      )}
      data-testid={dataTestId}
    >
      {children(fieldProps)}
    </div>
  );
}
